import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Download, Loader2, Paperclip, Pencil, Search, Sparkles, Trash2, Upload, X } from 'lucide-react'
import {
  fetchCaseLaw, searchCaseLaw, uploadCaseLaw, updateCaseLaw, deleteCaseLaw, downloadCaseLaw,
  CaseLawDigest, CaseLawUpdate,
} from '../../lib/api/api'
import FederalCaseLawTab from './FederalCaseLawTab'
import { useAuth } from '../../contexts/AuthContext'
import { useAttachments } from '../../contexts/AttachmentContext'
import { formatDate, formatNumber } from '../../shared/utils/format'

const PAGE_SIZE = 20

type EditForm = {
  title: string
  description: string
  case_number: string
  court_name: string
  decision_date: string
}

const toEditForm = (row: CaseLawDigest): EditForm => ({
  title: row.title || '',
  description: row.description || '',
  case_number: row.case_number || '',
  court_name: row.court_name || '',
  decision_date: row.decision_date ? row.decision_date.slice(0, 10) : '',
})

export default function CaseLawPage() {
  const { t } = useTranslation()
  const { canManageCorpus } = useAuth()
  const { attachDocument, detachDocument, isDocumentAttached } = useAttachments()

  const [tab, setTab] = useState<'rtid' | 'federal'>('rtid')

  const [items, setItems] = useState<CaseLawDigest[]>([])
  const [total, setTotal] = useState(0)
  const [offset, setOffset] = useState(0)
  const [textQuery, setTextQuery] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [semanticQuery, setSemanticQuery] = useState('')
  const [semanticResults, setSemanticResults] = useState<Array<Record<string, any>> | null>(null)
  const [isSearching, setIsSearching] = useState(false)

  const [expandedId, setExpandedId] = useState<number | null>(null)
  const [editingRow, setEditingRow] = useState<CaseLawDigest | null>(null)
  const [editForm, setEditForm] = useState<EditForm>({ title: '', description: '', case_number: '', court_name: '', decision_date: '' })
  const [isSaving, setIsSaving] = useState(false)

  const [isUploadOpen, setIsUploadOpen] = useState(false)
  const [uploadFile, setUploadFile] = useState<File | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [reloadToken, setReloadToken] = useState(0)

  useEffect(() => {
    let active = true
    setIsLoading(true)
    setError(null)
    fetchCaseLaw({ q: textQuery || undefined, limit: PAGE_SIZE, offset })
      .then((res) => {
        if (!active) return
        setItems(res.data.items)
        setTotal(res.data.total)
      })
      .catch(() => { if (active) setError(t('caseLaw.errors.loadFailed')) })
      .finally(() => { if (active) setIsLoading(false) })
    return () => { active = false }
  }, [textQuery, offset, reloadToken, t])

  const handleTextQueryChange = (value: string) => {
    setTextQuery(value)
    setOffset(0)
  }

  const runSemanticSearch = async () => {
    if (!semanticQuery.trim()) return
    setIsSearching(true)
    setError(null)
    try {
      const res = await searchCaseLaw(semanticQuery)
      setSemanticResults(res.data)
    } catch {
      setError(t('caseLaw.errors.loadFailed'))
    } finally {
      setIsSearching(false)
    }
  }

  const clearSemanticSearch = () => {
    setSemanticQuery('')
    setSemanticResults(null)
  }

  const handleAttachToggle = (row: CaseLawDigest) => {
    const id = String(row.id)
    if (isDocumentAttached(id, 'case_law_digest')) {
      detachDocument(id, 'case_law_digest')
    } else {
      attachDocument({
        id, title: row.title || row.case_number || `#${row.id}`,
        type: 'case_law_digest', doc_type: 'case_law_digest', file_name: row.file_name || '',
      })
    }
  }

  const handleDownload = async (row: CaseLawDigest) => {
    try {
      await downloadCaseLaw(row.id)
    } catch {
      setError(t('caseLaw.errors.downloadFailed'))
    }
  }

  const handleDelete = async (row: CaseLawDigest) => {
    if (!window.confirm(t('common.confirm'))) return
    try {
      await deleteCaseLaw(row.id)
      setItems((prev) => prev.filter((r) => r.id !== row.id))
    } catch {
      setError(t('caseLaw.errors.deleteFailed'))
    }
  }

  const openEdit = (row: CaseLawDigest) => {
    setEditingRow(row)
    setEditForm(toEditForm(row))
  }

  const handleSaveEdit = async () => {
    if (!editingRow) return
    setIsSaving(true)
    try {
      const body: CaseLawUpdate = {
        title: editForm.title,
        description: editForm.description,
        case_number: editForm.case_number,
        court_name: editForm.court_name,
        decision_date: editForm.decision_date || undefined,
      }
      const response = await updateCaseLaw(editingRow.id, body)
      setItems((prev) => prev.map((r) => (r.id === editingRow.id ? response.data : r)))
      setEditingRow(null)
    } catch {
      setError(t('caseLaw.errors.saveFailed'))
    } finally {
      setIsSaving(false)
    }
  }

  const handleUpload = async () => {
    if (!uploadFile) return
    setIsUploading(true)
    setError(null)
    try {
      const form = new FormData()
      form.append('file', uploadFile)
      await uploadCaseLaw(form)
      setUploadFile(null)
      setIsUploadOpen(false)
      setOffset(0)
      setReloadToken((v) => v + 1)
    } catch {
      setError(t('caseLaw.errors.uploadFailed'))
    } finally {
      setIsUploading(false)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-3xl font-bold text-gray-900 dark:text-gray-100">{t('caseLaw.title')}</h1>
        {canManageCorpus && tab === 'rtid' && (
          <button
            type="button"
            onClick={() => setIsUploadOpen((v) => !v)}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-primary-600 text-white text-sm"
          >
            <Upload size={16} />
            {t('common.upload')}
          </button>
        )}
      </div>

      <div role="tablist" className="flex border-b border-gray-200 dark:border-gray-700">
        {(['rtid', 'federal'] as const).map((key) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            onClick={() => setTab(key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === key ? 'border-primary-600 text-primary-700' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t(`caseLaw.tabs.${key}`)}
          </button>
        ))}
      </div>

      {tab === 'rtid' ? (<>

      {canManageCorpus && isUploadOpen && (
        <div className="p-4 border rounded-lg bg-white dark:bg-gray-800 space-y-3">
          <h2 className="text-sm font-medium">{t('caseLaw.upload.title')}</h2>
          <input
            type="file"
            accept=".pdf"
            onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
            aria-label={t('caseLaw.upload.file')}
          />
          <div>
            <button
              type="button"
              onClick={handleUpload}
              disabled={!uploadFile || isUploading}
              className="px-3 py-1.5 rounded-md bg-primary-600 text-white text-sm disabled:opacity-50"
            >
              {t('caseLaw.upload.submit')}
            </button>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={textQuery}
            onChange={(e) => handleTextQueryChange(e.target.value)}
            placeholder={t('caseLaw.filters.textPlaceholder')}
            className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800"
          />
        </div>
      </div>

      <div className="flex items-center gap-2">
        <input
          type="text"
          value={semanticQuery}
          onChange={(e) => setSemanticQuery(e.target.value)}
          placeholder={t('caseLaw.filters.textPlaceholder')}
          onKeyDown={(e) => { if (e.key === 'Enter') runSemanticSearch() }}
          className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800"
        />
        <button
          type="button"
          onClick={runSemanticSearch}
          disabled={isSearching}
          className="flex items-center gap-1 px-3 py-2 rounded-lg bg-purple-600 text-white text-sm disabled:opacity-50"
        >
          <Sparkles size={16} />
          {t('common.semanticSearch')}
        </button>
        {semanticResults && (
          <button type="button" onClick={clearSemanticSearch} className="text-sm text-gray-500 hover:text-gray-700">
            <X size={16} />
          </button>
        )}
      </div>

      {error && <p className="text-red-600 text-sm">{error}</p>}

      {semanticResults ? (
        <div className="space-y-2">
          <h2 className="text-sm font-medium text-gray-700">{t('caseLaw.semanticResults.title')}</h2>
          {semanticResults.length === 0 ? (
            <p className="text-gray-500 text-sm">{t('caseLaw.semanticResults.empty')}</p>
          ) : (
            <ul className="space-y-2">
              {semanticResults.map((hit, index) => (
                <li key={hit.id ?? index} className="p-3 border rounded-md bg-white dark:bg-gray-800">
                  <div className="font-medium">{hit.title ?? hit.doc_title}</div>
                  <div className="text-sm text-gray-500">{hit.case_number} {hit.court_name}</div>
                  {hit.content && <div className="text-sm text-gray-500 mt-1">{String(hit.content).slice(0, 240)}</div>}
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : (
        <>
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="animate-spin text-gray-400" />
            </div>
          ) : items.length === 0 ? (
            <p className="text-gray-500 text-center py-8">{t('caseLaw.list.noResults')}</p>
          ) : (
            <div className="space-y-2">
              {items.map((row) => {
                const attached = isDocumentAttached(String(row.id), 'case_law_digest')
                const expanded = expandedId === row.id
                return (
                  <div key={row.id} className="p-4 border rounded-lg bg-white dark:bg-gray-800">
                    <div className="flex items-start justify-between gap-4">
                      <button type="button" onClick={() => setExpandedId(expanded ? null : row.id)} className="flex-1 text-left">
                        <h3 className="font-medium text-gray-900 dark:text-gray-100">{row.title}</h3>
                        <div className="text-sm text-gray-500 mt-1 flex items-center gap-3 flex-wrap">
                          {row.case_number && <span>{row.case_number}</span>}
                          {row.court_name && <span>{row.court_name}</span>}
                          {row.decision_date && <span>{t('caseLaw.list.decisionDate', { date: formatDate(row.decision_date) })}</span>}
                        </div>
                      </button>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          type="button"
                          onClick={() => handleAttachToggle(row)}
                          title={attached ? t('common.detach') : t('common.attach')}
                          aria-label={attached ? t('common.detach') : t('common.attach')}
                          className={`p-2 rounded-full ${attached ? 'text-primary-600 bg-primary-50' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`}
                        >
                          <Paperclip size={18} />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDownload(row)}
                          title={t('common.download')}
                          aria-label={t('common.download')}
                          className="p-2 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                        >
                          <Download size={18} />
                        </button>
                        {canManageCorpus && (
                          <>
                            <button
                              type="button"
                              onClick={() => openEdit(row)}
                              title={t('common.edit')}
                              aria-label={t('common.edit')}
                              className="p-2 rounded-full text-gray-400 hover:text-primary-600 hover:bg-primary-50"
                            >
                              <Pencil size={18} />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDelete(row)}
                              title={t('common.delete')}
                              aria-label={t('common.delete')}
                              className="p-2 rounded-full text-gray-400 hover:text-red-600 hover:bg-red-50"
                            >
                              <Trash2 size={18} />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                    {expanded && row.description && (
                      <p className="mt-3 text-sm text-gray-600 border-t pt-3">{row.description}</p>
                    )}
                  </div>
                )
              })}
            </div>
          )}

          {total > PAGE_SIZE && (
            <div className="flex items-center justify-center gap-3 pt-2">
              <button
                type="button"
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                disabled={offset === 0}
                className="px-3 py-1 border rounded-md disabled:opacity-50"
              >
                {t('common.previous')}
              </button>
              <span className="text-sm text-gray-600">
                {t('common.showingRange', {
                  from: formatNumber(offset + 1),
                  to: formatNumber(Math.min(offset + PAGE_SIZE, total)),
                  total: formatNumber(total),
                })}
              </span>
              <button
                type="button"
                onClick={() => setOffset(offset + PAGE_SIZE)}
                disabled={offset + PAGE_SIZE >= total}
                className="px-3 py-1 border rounded-md disabled:opacity-50"
              >
                {t('common.next')}
              </button>
            </div>
          )}
        </>
      )}

      {editingRow && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-semibold">{t('caseLaw.edit.title')}</h3>
              <button type="button" onClick={() => setEditingRow(null)} className="text-gray-500 hover:text-gray-700">
                <X size={18} />
              </button>
            </div>
            <div className="p-4 space-y-3 overflow-y-auto">
              <input
                type="text"
                value={editForm.title}
                onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                aria-label={t('common.edit')}
                className="w-full border rounded-md px-3 py-1.5"
              />
              <textarea
                value={editForm.description}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                rows={4}
                className="w-full border rounded-md px-3 py-1.5"
              />
              <input
                type="text"
                value={editForm.case_number}
                onChange={(e) => setEditForm({ ...editForm, case_number: e.target.value })}
                placeholder={t('caseLaw.upload.caseNumber')}
                className="w-full border rounded-md px-3 py-1.5"
              />
              <input
                type="text"
                value={editForm.court_name}
                onChange={(e) => setEditForm({ ...editForm, court_name: e.target.value })}
                placeholder={t('caseLaw.upload.courtName')}
                className="w-full border rounded-md px-3 py-1.5"
              />
              <input
                type="date"
                value={editForm.decision_date}
                onChange={(e) => setEditForm({ ...editForm, decision_date: e.target.value })}
                aria-label={t('caseLaw.upload.decisionDate')}
                className="w-full border rounded-md px-3 py-1.5"
              />
            </div>
            <div className="flex justify-end gap-2 p-4 border-t">
              <button type="button" onClick={() => setEditingRow(null)} className="px-3 py-1.5 rounded-md border text-sm">
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={handleSaveEdit}
                disabled={isSaving}
                className="px-3 py-1.5 rounded-md bg-primary-600 text-white text-sm disabled:opacity-50"
              >
                {t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}
      </>) : <FederalCaseLawTab />}
    </div>
  )
}
